import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Button, Card, Container, Input, Label, Stack } from "@easymoving/ui";
import type { Locale } from "@easymoving/types";
import { useAuth } from "../context/AuthContext";
import { AuthError, signInWithEmail, signInWithGoogle, signUpWithEmail } from "../lib/auth";

type Mode = "login" | "signup";

export function AuthPage({ mode }: { mode: Mode }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);

  const target = (location.state as { from?: string } | null)?.from ?? "/app";
  const locale = (i18n.resolvedLanguage ?? "he").split("-")[0] as Locale;

  // Clickwrap consent at signup. Legal pages live on the landing site.
  const [agreed, setAgreed] = useState(false);
  const consentBlocked = mode === "signup" && !agreed;
  const legalUrl = (slug: "terms" | "privacy") =>
    `https://easymoving.io${locale === "he" ? "" : `/${locale}`}/${slug}`;

  // Race-free redirect: only navigate once the auth listener reports a user.
  useEffect(() => {
    if (user) navigate(target, { replace: true });
  }, [user, target, navigate]);

  const schema = z.object({
    name: z.string().trim().max(120).optional(),
    email: z
      .string()
      .min(1, t("auth.errors.required"))
      .email(t("auth.errors.emailInvalid")),
    password: z.string().min(6, t("auth.errors.passwordMin")),
  });
  type Form = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  async function onGoogle() {
    setFormError(null);
    setGoogleBusy(true);
    try {
      await signInWithGoogle(locale);
      // redirect handled by the effect above
    } catch (err) {
      const code = err instanceof AuthError ? err.code : "unknown";
      if (code !== "popup_closed") setFormError(t(`auth.errors.${code}`));
    } finally {
      setGoogleBusy(false);
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    setFormError(null);
    try {
      if (mode === "signup") {
        await signUpWithEmail({
          email: data.email,
          password: data.password,
          name: data.name,
          locale,
        });
      } else {
        await signInWithEmail({ email: data.email, password: data.password });
      }
      // redirect handled by the effect above
    } catch (err) {
      const code = err instanceof AuthError ? err.code : "unknown";
      setFormError(t(`auth.errors.${code}`));
    }
  });

  const isSignup = mode === "signup";

  return (
    <Container size="narrow" className="py-10 md:py-14">
      <Card padding="lg" className="max-w-md mx-auto">
        <Stack gap="md">
          <div>
            <h1 className="font-display text-2xl tracking-snug">
              {t(isSignup ? "auth.signupTitle" : "auth.loginTitle")}
            </h1>
            <p className="mt-1.5 text-graphite text-[15px]">
              {t(isSignup ? "auth.signupSubtitle" : "auth.loginSubtitle")}
            </p>
          </div>

          {isSignup && (
            <label className="flex items-start gap-2.5 text-[13px] text-graphite">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-petrol"
              />
              <span>
                {t("auth.agree")}{" "}
                <a href={legalUrl("terms")} target="_blank" rel="noreferrer" className="text-petrol underline underline-offset-2">
                  {t("auth.terms")}
                </a>
                {" · "}
                <a href={legalUrl("privacy")} target="_blank" rel="noreferrer" className="text-petrol underline underline-offset-2">
                  {t("auth.privacy")}
                </a>
              </span>
            </label>
          )}

          {/* Google — the primary sign-in. Email/password remains below. */}
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            disabled={googleBusy || consentBlocked}
            onClick={onGoogle}
          >
            <GoogleGlyph />
            {t("auth.google")}
          </Button>

          <div className="flex items-center gap-3 text-graphite-soft text-[12px]">
            <span className="h-px flex-1 bg-rule-strong" />
            {t("auth.or")}
            <span className="h-px flex-1 bg-rule-strong" />
          </div>

          <form onSubmit={onSubmit} noValidate>
            <Stack gap="sm">
              {isSignup && (
                <Label>
                  {t("auth.name")}
                  <Input
                    type="text"
                    autoComplete="name"
                    placeholder={t("auth.namePlaceholder")}
                    {...register("name")}
                  />
                </Label>
              )}
              <Label required>
                {t("auth.email")}
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  dir="ltr"
                  placeholder="you@example.com"
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
                {errors.email && (
                  <span className="text-[12px] text-clay">
                    {errors.email.message}
                  </span>
                )}
              </Label>
              <Label required>
                {t("auth.password")}
                <Input
                  type="password"
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  placeholder={t("auth.passwordPlaceholder")}
                  aria-invalid={!!errors.password}
                  {...register("password")}
                />
                {errors.password && (
                  <span className="text-[12px] text-clay">
                    {errors.password.message}
                  </span>
                )}
              </Label>

              {formError && (
                <p
                  role="alert"
                  className="text-[13px] text-clay-deep bg-clay-tint border border-clay/30 rounded-md px-3 py-2"
                >
                  {formError}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || consentBlocked}
                className="w-full mt-1"
              >
                {isSubmitting
                  ? t("auth.busy")
                  : t(isSignup ? "auth.submitSignup" : "auth.submitLogin")}
              </Button>
            </Stack>
          </form>

          <p className="text-center text-[14px] text-graphite">
            {t(isSignup ? "auth.toLoginPrompt" : "auth.toSignupPrompt")}{" "}
            <Link
              to={isSignup ? "/login" : "/signup"}
              state={location.state}
              className="text-petrol font-medium hover:underline underline-offset-4"
            >
              {t(isSignup ? "auth.toLoginLink" : "auth.toSignupLink")}
            </Link>
          </p>
        </Stack>
      </Card>
    </Container>
  );
}

function GoogleGlyph() {
  return (
    <svg className="size-[18px]" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
